import {
  Placeholder,
  VisitorIdentification,
} from '@sitecore-jss/sitecore-jss-react';
import React, {
  FC,
  useEffect,
  ReactNode,
  ReactElement,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { Helmet } from 'react-helmet';
import Modal from '_components/Modal';
import DigitalDataProvider from '_containers/DigitalDataContext';
import { LocationAPIContextProvider } from '_containers/LocationAPIContext';
import MemberProvider from '_containers/MemberContext';
import MenuProvider from '_containers/MenuContext';
import ModalProvider from '_containers/ModalContext';
import TimezoneProvider from '_containers/TimezoneContext';
import CountryProvider from '_containers/CountryContext';
import { getQueryStringValue } from '_utils/data';
import { getPropertyByKey } from '_utils/helpers/generics';
import {
  SpaLoader,
  Grid,
} from '_utils/components';
// Structured data for google
import { 
  CorpAllPagesStructuredData,
  SitelinkBoxHomePageStructuredData
} from '_utils/components/StructuredDataCorp'
import { INVISIBLE_CLASS, isMobileAppMode } from '_utils/helpers/render';
import { MainContainer } from '_layouts/SharedStyledLayout';

import { Language } from '_types';
import { TopicCategoryStructureData } from './topicCategoryStructure';

interface LayoutProps {
  context: any;
  route: any;
  loading?: boolean;
}

/**
 * DEFAULT APP LAYOUT
 */
const DefaultLayout: FC<LayoutProps> = ({
  context,
  route,
  loading
}): JSX.Element => {
  const language = getPropertyByKey<Language>('value', getPropertyByKey('languageType', route?.fields)) || null;

  const { search } = useLocation();

    const [shouldLoadCxOne, setShouldLoadCxOne] = useState(false);

  useEffect(() => {
    // Check cookie only client-side
    if (isMobileAppMode()) {
      setShouldLoadCxOne(true);
    }
  }, []);
  useEffect(() => {
    const fileToDownload = getQueryStringValue('file', search);

    if (!loading && fileToDownload) {
      window.location.href = fileToDownload;
    }
  }, [search, loading]);

  // Temporary solution for new chat
  const enableNewChat = context?.siteSearchSettings?.enableNewChat

  return (
    <>
      <MenuProvider>
        <ModalProvider>
          <MemberProvider>
            <DigitalDataProvider>
              <TimezoneProvider>
                <CountryProvider>
                <LocationAPIContextProvider>
                  <Placeholder name="jss-header" rendering={route} />
                  {/*
                    Persistent zone rendered OUTSIDE MainContainer and the `loading`
                    invisible wrapper. Components here (e.g. breadcrumb + dropdown ribbon)
                    keep a stable position across client-side route changes, so they are
                    not hidden/re-rendered on navigation like jss-content-intro is.
                  */}
                  <Placeholder name="jss-subheader" rendering={route} />
                  <MainContainer
                    id="main-content"
                    lang={language}
                    name="jss-main"
                    rendering={route}
                    role="main"
                  >
                    <SpaLoader />

                    {/* prevents flash reverted content when route change and personalisation applied */}
                    <div className={loading ? INVISIBLE_CLASS : ''}>
                      <Placeholder name="jss-content-intro" rendering={route} />
                      <Placeholder name="jss-content-main" rendering={route} />

                      <Placeholder
                        name="jss-content-footer"
                        rendering={route}
                        render={(components: ReactNode[]) =>
                          <>
                            {components
                              .filter((component: ReactNode) => (component !== null && component !== undefined))
                              .map((component: ReactElement, index: number) => {

                                if (component.props && component.props?.type !== 'text/sitecore') {
                                  return <Grid.Container key={index}>
                                    <Grid.Row>
                                      <Grid.Item>{components}</Grid.Item>
                                    </Grid.Row>
                                  </Grid.Container>
                                }
                                return component;
                              })}
                          </>
                        }
                      />

                      <Placeholder name="jss-content-outro" rendering={route} />
                    </div>

                  </MainContainer>
                  <Placeholder name="jss-footer" rendering={route} />
                  <Modal />
                  {/* Google Structured data */}
                  <SitelinkBoxHomePageStructuredData context={context}/>
                  <CorpAllPagesStructuredData context={context} />
                  <TopicCategoryStructureData context={context} rendering={route} />
                  {!shouldLoadCxOne && (
                  <Helmet>
                    <script>
                      {`
                        (function (n, u) {
                          (window.CXoneDfo = n),
                            (window[n] =
                              window[n] ||
                              function () {
                                (window[n].q = window[n].q || []).push(arguments);
                              }),
                            (window[n].u = u),
                            (e = document.createElement("script")),
                            (e.type = "module"),
                            (e.src = u + "?" + Math.round(Date.now() / 1e3 / 3600)),
                            document.head.appendChild(e);
                        })(
                          "cxone",
                          "https://web-modules-de-au1.niceincontact.com/loader/1/loader.js"
                        );

                        cxone("init", "1120");
                        cxone("guide", "init");
                      `}
                    </script>
                  </Helmet>
                  )}
                </LocationAPIContextProvider>
                </CountryProvider>
              </TimezoneProvider>
            </DigitalDataProvider>
          </MemberProvider>
        </ModalProvider>
      </MenuProvider>
      <VisitorIdentification />
    </>
  );
};

export default DefaultLayout;
